import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Select,
  Space,
  Tooltip,
  message,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { IndoorPositioningEquipment } from "../../../../api";
import RoleShape from "./RoleShape";
import { canonicalDeviceId, deviceIdRule } from "../../../../utils/deviceId";

const { Option } = Select;

const ROLE_LABELS = {
  head: "頭",
  tail: "尾",
};

// 一台機具最多只有車頭＋車尾兩顆定位器。
const MAX_TAGS_PER_EQUIPMENT = 2;

// 頭/尾各自只能有一顆定位器，超過時後端會回傳中文 400 錯誤；
// 這裡先在下拉選單擋掉已被使用的角色，避免使用者送出後才被打回。
const roleIsTaken = (role, tags, excludeTagId) =>
  (role === "head" || role === "tail") &&
  tags.some((tag) => tag.role === role && tag.id !== excludeTagId);

const EquipmentTagsModal = ({ open, equipment, onClose, onChanged }) => {
  const [form] = Form.useForm();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const tags = equipment?.tags || [];

  useEffect(() => {
    if (!formModalOpen) return;
    if (editingTag) {
      form.setFieldsValue(editingTag);
    } else {
      form.resetFields();
    }
  }, [formModalOpen, editingTag]);

  const handleAdd = () => {
    setEditingTag(null);
    setFormModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingTag(record);
    setFormModalOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "確認刪除",
      content: `確定要刪除定位器「${record.name}」嗎？`,
      okText: "確認",
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await IndoorPositioningEquipment.DeleteTag(
            record.id,
          );
          if (response.code === 200) {
            message.success("刪除定位器成功");
            onChanged();
          } else {
            message.error(response.message || "刪除定位器失敗");
          }
        } catch (error) {
          console.error("刪除定位器失敗:", error);
          message.error("刪除定位器失敗");
        }
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (roleIsTaken(values.role, tags, editingTag?.id)) {
        message.error(
          `此機具已有${ROLE_LABELS[values.role]}定位器，每台機具僅能設定一個${ROLE_LABELS[values.role]}`,
        );
        return;
      }

      const payload = { ...values, device_id: canonicalDeviceId(values.device_id) };

      setSubmitting(true);
      const response = editingTag
        ? await IndoorPositioningEquipment.UpdateTag(editingTag.id, payload)
        : await IndoorPositioningEquipment.CreateTag(equipment.id, payload);

      if (response.code === 200) {
        message.success(editingTag ? "更新定位器成功" : "新增定位器成功");
        setFormModalOpen(false);
        form.resetFields();
        setEditingTag(null);
        onChanged();
      } else {
        message.error(
          response.message ||
            (editingTag ? "更新定位器失敗" : "新增定位器失敗"),
        );
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error("提交定位器失敗:", error);
      message.error(editingTag ? "更新定位器失敗" : "新增定位器失敗");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "名稱", dataIndex: "name", key: "name", align: "center" },
    { title: "裝置 ID", dataIndex: "device_id", key: "device_id", align: "center" },
    {
      title: "角色",
      dataIndex: "role",
      key: "role",
      align: "center",
      render: (role) => (
        <Space size={6}>
          <RoleShape role={role} />
          {ROLE_LABELS[role] || role}
        </Space>
      ),
    },
    {
      title: "操作",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={equipment ? `定位器管理－${equipment.name}` : "定位器管理"}
        open={open}
        onCancel={onClose}
        footer={null}
        width={700}
        destroyOnClose
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span>共 {tags.length} 個定位器（車頭/車尾各限一個）</span>
          <Tooltip
            title={
              tags.length >= MAX_TAGS_PER_EQUIPMENT
                ? "一台機具最多只能有車頭＋車尾兩個定位器"
                : ""
            }
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={tags.length >= MAX_TAGS_PER_EQUIPMENT}
            >
              新增定位器
            </Button>
          </Tooltip>
        </div>
        <Table
          columns={columns}
          dataSource={tags}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: "暫無定位器" }}
        />
      </Modal>

      <Modal
        title={editingTag ? "編輯定位器" : "新增定位器"}
        open={formModalOpen}
        onCancel={() => {
          setFormModalOpen(false);
          form.resetFields();
          setEditingTag(null);
        }}
        onOk={handleSubmit}
        okText="確認"
        cancelText="取消"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="定位器名稱"
            name="name"
            rules={[{ required: true, message: "請輸入定位器名稱" }]}
          >
            <Input placeholder="例如：車頭、車尾" />
          </Form.Item>
          <Form.Item
            label="裝置 ID"
            name="device_id"
            rules={[
              { required: true, message: "請輸入裝置 ID" },
              deviceIdRule,
            ]}
          >
            <Input placeholder="請輸入裝置 ID (例如: AABBCCDDEEFF)" />
          </Form.Item>
          <Form.Item
            label="角色"
            name="role"
            rules={[{ required: true, message: "請選擇角色" }]}
          >
            <Select placeholder="請選擇角色">
              <Option
                value="head"
                disabled={roleIsTaken("head", tags, editingTag?.id)}
              >
                <Space size={6}>
                  <RoleShape role="head" />頭
                  {roleIsTaken("head", tags, editingTag?.id) && "（已被使用）"}
                </Space>
              </Option>
              <Option
                value="tail"
                disabled={roleIsTaken("tail", tags, editingTag?.id)}
              >
                <Space size={6}>
                  <RoleShape role="tail" />尾
                  {roleIsTaken("tail", tags, editingTag?.id) && "（已被使用）"}
                </Space>
              </Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EquipmentTagsModal;
